import { useId, useState } from 'react';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { formatMoney, todayIso } from '../format';
import type { PaymentRecordItem } from '../types';

const NINE_DIGITS = /\d{9,}/;

/**
 * Mark an Approved Escochex record as Paid. Paid means "recorded as paid": it moves no money and prints or mails nothing. The date is when the payment was
 * actually completed or reconciled (not before approval and not in the future: the server decides the lower bound). BUSINESS RULE CONFIRMATION REQUIRED:
 * when a check becomes Paid (issuance, mailing, clearance, reconciliation) is unresolved, so this is controlled manual reconciliation.
 */
export function MarkPaidDialog({ record, onClose, onConfirm }: {
  record: PaymentRecordItem;
  onClose: () => void;
  /** Resolves to an error message, or null when the record was saved (the page then closes this dialog). */
  onConfirm: (paidOn: string, reference: string | null) => Promise<string | null>;
}) {
  const uid = useId();
  const [paidOn, setPaidOn] = useState(todayIso());
  const [reference, setReference] = useState('');
  const [errors, setErrors] = useState<{ paidOn?: string; reference?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    const found: typeof errors = {};
    if (!/^\d{4}-\d{2}-\d{2}$/.test(paidOn)) found.paidOn = 'Enter the date the payment was completed.';
    else if (paidOn > todayIso()) found.paidOn = 'The paid date can’t be in the future.';
    if (reference.length > 100) found.reference = 'The reference must be 100 characters or fewer.';
    else if (NINE_DIGITS.test(reference)) found.reference = 'Don’t enter account or routing numbers in the reference.';
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setBusy(true);
    setError(null);
    const refusal = await onConfirm(paidOn, reference.trim() || null);
    if (refusal) {
      setError(refusal);
      setBusy(false);
    }
  }

  return (
    <ModalDialog
      title="Mark as Paid"
      size="sm"
      className="cp-sheet"
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="jr-btn jr-btn--primary" onClick={() => void submit()} disabled={busy}>{busy ? 'Saving…' : 'Mark as Paid'}</button>
        </>
      )}
    >
      <p>
        Record that the {formatMoney(record.amount)} payment to <strong>{record.contractorName}</strong> ({record.paymentId}) was completed. This only updates the record:
        no money is moved and nothing is printed or mailed.
      </p>
      <div className="jr-field">
        <label htmlFor={`${uid}-date`}>Paid date <span aria-hidden="true" className="jr-req">*</span></label>
        <input id={`${uid}-date`} type="date" data-autofocus value={paidOn} max={todayIso()} aria-required="true" aria-invalid={errors.paidOn ? true : undefined} onChange={(e) => setPaidOn(e.target.value)} />
        {errors.paidOn && <p className="jr-error" role="alert">{errors.paidOn}</p>}
      </div>
      <div className="jr-field">
        <label htmlFor={`${uid}-ref`}>Reference (optional)</label>
        <input id={`${uid}-ref`} value={reference} maxLength={100} placeholder="For example: cleared the bank on the 22nd" aria-invalid={errors.reference ? true : undefined} onChange={(e) => setReference(e.target.value)} />
        {errors.reference && <p className="jr-error" role="alert">{errors.reference}</p>}
      </div>
      {error && <p className="jr-error" role="alert" data-testid="markpaid-error">{error}</p>}
    </ModalDialog>
  );
}
